import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import { Button, DialogRoot } from '@gitbook/spine-react';

import { useSitePublish } from './useSitePublish';

export function SitePublishButton(props: {
    site: api.Site;
    organization: api.Organization;
    canAdminSite: boolean;
    onPress?: () => void;
}) {
    const { site, organization, canAdminSite, onPress } = props;
    const publish = useSitePublish({ organization, onPress });

    const buttonProps = getSitePublishButtonProps({
        site,
        organization,
        canAdminSite,
        onPressPublish: publish.publishSite,
    });

    return (
        <DialogRoot {...publish.dialogRootProps}>
            <Button {...buttonProps} />
            {publish.publishDialogElement}
        </DialogRoot>
    );
}

function getSitePublishButtonProps(props: {
    site: api.Site;
    organization: api.Organization;
    canAdminSite: boolean;
    onPressPublish: (site: api.Site) => Promise<void>;
}) {
    const { site, organization, canAdminSite, onPressPublish } = props;

    if (site.published) {
        if (
            [api.SiteVisibility.Public, api.SiteVisibility.Unlisted].includes(site.visibility) &&
            site.urls.published
        ) {
            return {
                label: 'Visit',
                leadingIcon: Icon.ExternalLink,
                href: site.urls.published || '',
                target: '_blank',
                onPress: () => {},
            };
        }

        if (site.visibility === api.SiteVisibility.ShareLink) {
            return {
                label: 'Share links',
                leadingIcon: Icon.PublishStatusShareLink,
                href: {
                    overlay: null,
                    screen: {
                        type: 'site' as const,
                        organization: organization.id,
                        site: site.id,
                        tab: {
                            type: 'settings' as const,
                            section: 'audience' as const,
                        },
                    },
                },
                onPress: () => {
                    analytics.logEvent(
                        analytics.createSiteEvent(
                            { id: site.id, organization: organization.id },
                            { type: 'site_manage_share_links', triggerLocation: 'header' }
                        )
                    );
                },
            };
        }

        if (site.visibility === api.SiteVisibility.VisitorAuth) {
            return {
                label: 'Authenticated access',
                leadingIcon: Icon.PublishStatusVisitorAuth,
                href: {
                    overlay: null,
                    screen: {
                        type: 'site' as const,
                        organization: organization.id,
                        site: site.id,
                        tab: {
                            type: 'settings' as const,
                            section: 'audience' as const,
                        },
                    },
                },
                onPress: () => {
                    analytics.logEvent(
                        analytics.createSiteEvent(
                            { id: site.id, organization: organization.id },
                            {
                                type: 'site_manage_authenticated_access',
                                triggerLocation: 'header',
                            }
                        )
                    );
                },
            };
        }
    }

    // Not published - show publish button
    const notPublishableReason: string | null = (() => {
        if (!canAdminSite) {
            return 'You need creator permissions or higher on a linked space to publish this site';
        }
        if (site.siteSpaces <= 0) {
            return 'Link a space to publish this site';
        }
        return null;
    })();

    return {
        label: 'Publish',
        leadingIcon: Icon.Publish,
        disabled: Boolean(notPublishableReason),
        title: notPublishableReason ?? undefined,
        loadable: true,
        onPress: async () => {
            await onPressPublish(site);
        },
    };
}
